
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useToast } from "@/components/ui/use-toast";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import FormFields from "./FormFields";
import { useState } from "react";

const formSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  phone: z.string().min(8, "Please enter a valid phone number with country code"),
  email: z.string().email("Please enter a valid email address"),
  country: z.string().min(2, "Please select your country"),
  product: z.string().optional(),
  requirements: z.string().min(10, "Requirements must be at least 10 characters"),
});

type FormValues = z.infer<typeof formSchema>;

const ContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      companyName: "",
      phone: "",
      email: "",
      country: "",
      product: "",
      requirements: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    
    // Replace YOUR_ACCESS_KEY with your actual Web3Forms access key
    const formData = {
      access_key: "59e9a68c-6f3f-4cd0-996e-0044fb9e0f56",
      name: values.fullName,
      company: values.companyName,
      phone: values.phone,
      email: values.email,
      country: values.country,
      product: values.product || "Not specified",
      message: values.requirements,
      subject: "New Contact Form Inquiry",
    };

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.status === 200) {
        toast({
          title: "Inquiry Submitted Successfully!",
          description: "We will get back to you soon.",
        });
        form.reset();
      } else {
        throw new Error(result.message || "Something went wrong!");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit the form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-sm">
      <h2 className="text-2xl font-semibold mb-6">Send us an Inquiry</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormFields form={form} />
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit Inquiry"}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;
